import {
  auditClient,
  billingClient,
  bpmClient,
  careClient,
  connectClient,
  createHttp,
  creditClient,
  customerClient,
  dmsClient,
  fileClient,
  forecastClient,
  hrClient,
  iamClient,
  identityClient,
  insightClient,
  inventoryClient,
  inventoryOpsClient,
  inventoryTraceClient,
  logisticsClient,
  loyaltyClient,
  monitoringClient,
  notifyClient,
  oemClient,
  orderClient,
  orderConfigClient,
  organizationClient,
  paymentClient,
  platformBpmClient,
  platformIamClient,
  platformIdentityClient,
  platformOpsClient,
  portalClient,
  pricingClient,
  productClient,
  productionClient,
  salesClient,
  salesCommissionClient,
  searchClient,
  socialClient,
  supplyClient,
  tenantClient,
} from '@kovix/api-client';
import { activeSupportToken, getAccessToken, reauthenticate, setSupportToken } from './auth';
import { currentLocale, intlTag } from './i18n';
import { useSession } from './session';

/** Một instance HTTP cho toàn app; base = gateway (dev proxy /api → :8000). */
export const http = createHttp({
  baseUrl:
    import.meta.env.VITE_API_BASE && import.meta.env.VITE_API_BASE !== '/'
      ? import.meta.env.VITE_API_BASE
      : window.location.origin,
  tokenProvider: getAccessToken,
  timeoutMs: 10_000,
  extraHeaders: () => ({ 'Accept-Language': intlTag(currentLocale()) }),
  onUnauthorized: (path) => {
    // R6.13 K-20: 401 trên API tenant khi đang dùng token phiên hỗ trợ = phiên đã kết thúc/thu hồi/hết hạn ở gateway
    // (auth.support_session_ended) → bỏ token phiên, về lại chế độ platform; KHÔNG đăng nhập lại (token platform vẫn còn).
    if (activeSupportToken() && !(path ?? '').startsWith('/api/v1/platform/')) {
      setSupportToken(null);
      // KOVIX-2981 B8: ghi lý do để Shell báo "phiên hỗ trợ đã kết thúc" + đưa về phạm vi Platform (trước đây im lặng, màn tenant thành Không có quyền)
      useSession.getState().endSupport('ended');
      return;
    }
    // Đăng nhập lại rồi quay về đúng trang; vừa đăng nhập lại mà vẫn 401 → không chuyển hướng, ApiError 401 hiện ở màn hình
    reauthenticate();
  },
});

export const api = {
  product: productClient(http),
  customer: customerClient(http),
  iam: iamClient(http),
  pricing: pricingClient(http),
  credit: creditClient(http),
  bpm: bpmClient(http),
  inventory: inventoryClient(http),
  order: orderClient(http),
  sales: salesClient(http),
  inventoryOps: inventoryOpsClient(http),
  inventoryTrace: inventoryTraceClient(http),
  orderConfig: orderConfigClient(http),
  supply: supplyClient(http),
  logistics: logisticsClient(http),
  production: productionClient(http),
  oem: oemClient(http),
  connect: connectClient(http),
  notify: notifyClient(http),
  file: fileClient(http),
  audit: auditClient(http),
  payment: paymentClient(http),
  social: socialClient(http),
  loyalty: loyaltyClient(http),
  salesCommission: salesCommissionClient(http), // BRD 4.0 hoa hồng bán hàng
  care: careClient(http),
  dms: dmsClient(http),
  hr: hrClient(http),
  tenant: tenantClient(http), // W8 — control plane 5.2
  billing: billingClient(http),
  platformOps: platformOpsClient(http), // W9 — 5.3 platform-ops đầy đủ
  monitoring: monitoringClient(http), // W9 — 5.4 monitoring-svc
  insight: insightClient(http), // W10 — 5.5 insight-svc (báo cáo, dashboard, metric, KPI, lịch gửi)
  forecast: forecastClient(http), // W10 — 5.5 forecast-svc (12 mô hình dự báo, khuyến nghị có kiểm soát)
  search: searchClient(http), // W11 — tìm kiếm toàn cục (search-svc, OpenSearch)
  platformBpm: platformBpmClient(http), // R5.2 — /api/v1/platform/bpm: mẫu dùng chung + quy trình nền tảng (chỉ đọc)
  platformIam: platformIamClient(http), // X3 — /api/v1/platform/iam: quản trị viên nền tảng, vai trò/SoD platform, chính sách bảo mật control plane
  organization: organizationClient(http), // Y6 — tổ chức mua hàng, KYB, yêu cầu sửa hồ sơ (customer-svc)
  portal: portalClient(http), // Y6 — portal-svc: hàng đợi đăng ký, membership, vai trò Portal, chính sách (chỉ /admin)
  identity: identityClient(http), // Y6 — identity-svc: projection KOVIX ID của tenant (chỉ /tenant)
  platformIdentity: platformIdentityClient(http), // Q1 A4 — /api/v1/platform/identity: khóa/gỡ khóa toàn cục KOVIX ID + sổ bảo mật (token platform)
};
